/**
 * Engagement + use-type phrasing — the composed, client-facing service line.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  The quote's service line reads as a plain-English engagement statement:
 *      `${serviceName} — ${engagementPhrase} for your ${qualifier} ${useType}`
 *  e.g.  "The Alignment Path · Nidana — Vastu Review and Recommendations
 *         for your existing Residence"
 *
 *  Two OWNER-EDITABLE maps drive it (tweak the wording here, nothing else):
 *   • ENGAGEMENT_PHRASES — per ServiceTier: the engagement phrase + the
 *     property qualifier ("new" / "existing" / "proposed").
 *   • USE_TYPE_PHRASES — per use type: the noun for the property.
 *
 *  PURE — no Firebase, no React. Reused by the quote detail view + the send/
 *  message preview so the client sees ONE composed line everywhere.
 * ─────────────────────────────────────────────────────────────────────────
 */
import type { ServiceTier } from './rateCard';
import type { ProjectType } from './propertyModel';

/* ════════════════════ Use type ════════════════════ */

/**
 * The quote-layer use type = the propertyModel `ProjectType` PLUS `'plot'`
 * (raw land, which the property model doesn't carry). Kept LOCAL to the quote
 * layer on purpose: widening `ProjectType` itself would ripple through intake /
 * palettes / labels, so `'plot'` is handled here only.
 */
export type UseType = ProjectType | 'plot';

/** OWNER-EDITABLE — the client-facing noun for each use type. */
export const USE_TYPE_PHRASES: Record<UseType, string> = {
  residence: 'Residence',
  office: 'Office',
  commercial: 'Commercial Space',
  retail: 'Retail Space',
  industrial: 'Industrial Unit',
  institutional: 'Institutional Space',
  plot: 'Plot / Land',
};

/** The dropdown options for the quote form (value + label), in display order. */
export const USE_TYPE_OPTIONS: { value: UseType; label: string }[] = [
  { value: 'residence', label: USE_TYPE_PHRASES.residence },
  { value: 'office', label: USE_TYPE_PHRASES.office },
  { value: 'commercial', label: USE_TYPE_PHRASES.commercial },
  { value: 'retail', label: USE_TYPE_PHRASES.retail },
  { value: 'industrial', label: USE_TYPE_PHRASES.industrial },
  { value: 'institutional', label: USE_TYPE_PHRASES.institutional },
  { value: 'plot', label: USE_TYPE_PHRASES.plot },
];

/**
 * PURE — normalise a free-text property-type string (as stored on a lead's
 * `propertyType`) to a `UseType`, or null when nothing matches (→ omit the use
 * type gracefully). Keyword match, case-insensitive; never throws.
 */
export function normalizeUseType(raw: string | null | undefined): UseType | null {
  const s = (raw ?? '').trim().toLowerCase();
  if (!s) return null;
  if (/\b(plot|land|site|parcel|vacant)\b/.test(s)) return 'plot';
  if (/\b(resid|home|house|flat|apartment|villa|bungalow|dwelling)\b/.test(s)) return 'residence';
  if (/\b(retail|shop|store|showroom|mall)\b/.test(s)) return 'retail';
  if (/\b(office|workspace|co-?work)\b/.test(s)) return 'office';
  if (/\b(industr|factory|warehouse|plant|manufactur)\b/.test(s)) return 'industrial';
  if (/\b(institut|school|college|hospital|temple|trust|govt|government)\b/.test(s)) return 'institutional';
  if (/\b(commercial|business|corporate)\b/.test(s)) return 'commercial';
  return null;
}

/** PURE — the client-facing noun for a use type (null → no phrase). */
export function useTypePhrase(useType: UseType | null | undefined): string | null {
  return useType ? USE_TYPE_PHRASES[useType] : null;
}

/** PURE — runtime guard: is a raw value one of the known use types? */
export function isUseType(v: unknown): v is UseType {
  return typeof v === 'string' && Object.prototype.hasOwnProperty.call(USE_TYPE_PHRASES, v);
}

/* ════════════════════ Engagement phrase (per tier) ════════════════════ */

export interface EngagementPhrase {
  /** The engagement statement, e.g. "Vastu Review and Recommendations". */
  phrase: string;
  /** The property qualifier — "new" / "existing" / "proposed". */
  qualifier: string;
}

/**
 * OWNER-EDITABLE — the engagement phrase + qualifier per ServiceTier. Only the
 * nine real tiers (L1A..L3B) carry a phrase; CUSTOM / add-ons fall through to
 * no composed description (the service line is just the serviceName). Tweak the
 * wording here whenever the offering language changes.
 */
export const ENGAGEMENT_PHRASES: Partial<Record<ServiceTier, EngagementPhrase>> = {
  L1A: { phrase: 'Vastu Selection and Recommendations', qualifier: 'new' },
  L1B: { phrase: 'Vastu Review and Recommendations', qualifier: 'existing' },
  L1C: { phrase: 'Vastu Plan Review and Recommendations', qualifier: 'existing' },
  L2A: { phrase: 'Vastu Layout and Recommendations', qualifier: 'proposed' },
  L2B: { phrase: 'Vastu Review and Recommendations', qualifier: 'existing' },
  L2C: { phrase: 'Vastu Coordination and Recommendations', qualifier: 'proposed' },
  L2D: { phrase: 'Vastu Execution Drawings', qualifier: 'proposed' },
  L3A: { phrase: 'Vastu Design Brief and Recommendations', qualifier: 'proposed' },
  L3B: { phrase: 'Vastu-Based Design and Delivery', qualifier: 'proposed' },
};

/** PURE — the engagement phrase for a tier code (string-tolerant); null when none. */
export function engagementPhraseFor(tier: string): EngagementPhrase | null {
  return ENGAGEMENT_PHRASES[tier as ServiceTier] ?? null;
}

/* ════════════════════ Composition ════════════════════ */

/**
 * PURE — the composed service DESCRIPTION:
 *   `${engagementPhrase} for your ${qualifier} ${useTypePhrase}`
 * Trims gracefully when the use type is missing → `for your ${qualifier}
 * property`. Returns '' when the tier has no engagement phrase (→ caller falls
 * back to the bare serviceName). Never leaves a dangling "for your  ".
 */
export function composeServiceDescription(
  tier: string,
  useType: UseType | null | undefined,
): string {
  const e = engagementPhraseFor(tier);
  if (!e) return '';
  const noun = useTypePhrase(useType) ?? 'property';
  return `${e.phrase} for your ${e.qualifier} ${noun}`.replace(/\s+/g, ' ').trim();
}

/**
 * PURE — the full composed service LINE for a quote:
 *   `${serviceName} — ${serviceDescription}`
 * A non-empty MANUAL `serviceDescription` on the quote OVERRIDES the composed
 * description (so a hand-written service line always wins). When there is no
 * description at all, the line is just the serviceName (never a dangling "— ").
 */
export function composeQuoteServiceLine(quote: {
  serviceName: string;
  tier: string;
  useType?: UseType | null;
  serviceDescription?: string | null;
}): string {
  const manual = (quote.serviceDescription ?? '').trim();
  const desc = manual || composeServiceDescription(quote.tier, quote.useType ?? null);
  const name = (quote.serviceName ?? '').trim();
  if (!desc) return name;
  return name ? `${name} — ${desc}` : desc;
}
